import moment from "moment";
import constant from "../Constant";
import * as Sentry from "@sentry/react";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import { trackEvent } from "./mixpanel";
import { DEVICE_ID_KEY } from "./device-id";
import { LANGUAGE_CHOICE_KEY } from "./languagePreference";

const { REACT_APP_ENV } = process.env;

export const getGlobalInfoCookieName = () => {
  let name = "";
  if (REACT_APP_ENV === "prod") {
    name = "user";
  } else {
    name = `user-${REACT_APP_ENV}`;
  }
  return name;
};

export const deleteCookie = (name) => {
  var date = new Date();
  document.cookie =
    name +
    "=;expires=" +
    date.toGMTString() +
    ";domain=" +
    constant.APP_DOMAIN +
    ";path=/";
};

export const setGlobalInfoCookie = (global_data) => {
  const name = getGlobalInfoCookieName();
  deleteCookie(name);
  var cookieValue = JSON.stringify(global_data);
  var myDate = new Date();
  myDate.setMonth(myDate.getMonth() + 12);
  document.cookie =
    name +
    "=" +
    cookieValue +
    ";expires=" +
    myDate +
    ";domain=" +
    constant.APP_DOMAIN +
    ";path=/";
};

export const deleteGlobalInfoCookie = () => {
  const name = getGlobalInfoCookieName();
  deleteCookie(name);
};

export const clearStorage = () => {
  // WONE-569: the userEmailId preserve hack is retired. Identity is
  // sourced from ``GET /api/v1/auth/me`` keyed by the in-memory bearer,
  // so a clean localStorage no longer wedges the post-redeem profile
  // load. WONE-565 (Phase 4b-4) had already retired the legacy
  // CryptoJS-AES "keepMeSignInData" cache.
  //
  // The data-platform install id must outlive this wipe: it's a stable
  // per-device identifier, not session state. clearStorage() runs on logout
  // *and* mid-session in the SSO callback — losing it there would sever the
  // anonymous-open → sign-in stitch and reset the app.opened throttle.
  // The chosen language must outlive this wipe for the same reason: it is a
  // per-device preference, not session state. It also matters for
  // correctness — clearStorage() runs mid-session in the SSO callback, so
  // wiping it there would silently discard a language picked on the sign-in
  // screen before it ever reached the profile, which is the whole point of
  // offering the picker before sign-in. Mobile keeps it across sign-out too.
  const preservedKeys = [DEVICE_ID_KEY, LANGUAGE_CHOICE_KEY];
  const preserved = preservedKeys
    .map((key) => [key, localStorage.getItem(key)])
    .filter(([, value]) => value !== null);
  localStorage.clear();
  preserved.forEach(([key, value]) => localStorage.setItem(key, value));
  deleteGlobalInfoCookie();
};

export const capitalizeFirstLetter = (string) => {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
};

export const validateEmail = (val) => {
  const emailRex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRex.test(val);
};

export const getDayType = (session) => {
  const isSameStartDate = moment
    .utc(session?.instanceStartDateTime || session?.startDateTime)
    .isSame(moment(), "day");
  return isSameStartDate ? "today" : "weekday";
};

export const toKebabCase = (text) => {
  return text?.replace(
    /[A-Z]/g,
    (match, offset) => (offset > 0 ? "-" : "") + match.toLowerCase()
  );
};

export const toPascalCase = (text) => {
  return text.replace(/(^\w|-\w)/g, clearAndUpper);
};

function clearAndUpper(text) {
  return text.replace(/-/, "").toUpperCase();
}

export function logDebug(message, ...optionalParams) {
  if (process.env.REACT_APP_ENV !== "prod") {
    console.debug(message, ...optionalParams);
  }
}

export async function logError(message, error, ...optionalParams) {
  if (process.env.REACT_APP_ENV === "dev") {
    console.error(message, error, ...optionalParams);
  } else {
    if (error instanceof Error) {
      Sentry.captureException(error);
      trackEvent("Error Logged", {
        Error: error,
        Message: error.message,
        Fatal: "No",
      });
      logAnalyticsEvent("exception", {
        message: error.message,
      });
    } else {
      error = new Error(error);
      Sentry.captureException(error);
      trackEvent("Error Logged", {
        Error: error,
        Message: error.message,
        Fatal: "No",
      });
      logAnalyticsEvent("exception", {
        message: error.message,
      });
    }
  }
}

export function logErrors(message, errors, ...optionalParams) {
  for (const error of errors) {
    logError(message, error, ...optionalParams);
  }
}

/**
 * Returns the ordinal representation of a given session count (e.g., 1st, 2nd, 3rd, 4th).
 *
 * @param {number} sessionCount - The session number to convert to an ordinal string.
 * @returns {string} The session count with its appropriate ordinal suffix.
 */
export function sessionOrdinalCount(sessionCount) {
  const j = sessionCount % 10,
    k = sessionCount % 100;
  if (j === 1 && k !== 11) return `${sessionCount}st`;
  if (j === 2 && k !== 12) return `${sessionCount}nd`;
  if (j === 3 && k !== 13) return `${sessionCount}rd`;
  return `${sessionCount}th`;
}

/**
 * Converts a string to sentence case, capitalizing the first letter of each word and making the rest lowercase.
 *
 * @param {string} str - The input string to be converted.
 * @returns {string} The sentence-cased string.
 */
export function sentenceCase(str) {
  const nameParts = str.trim().split(" ");
  const capitalizedParts = nameParts.map(
    (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
  );

  return capitalizedParts.join(" ");
}
